import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Video,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  CheckCircle2,
  XCircle,
  Link as LinkIcon,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  Play,
  Loader2,
  ExternalLink,
  MessageSquare,
  Star
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import DashboardLayout from "../../../components/DashboardLayout";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    scheduled: "bg-blue-100 text-blue-700 border-blue-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-100 text-red-700 border-red-200"
  };

  const labels = {
    pending: "Pendiente",
    scheduled: "Agendado",
    completed: "Completado",
    cancelled: "Cancelado"
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const SimulationCard = ({ session, onUpdate, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [zoomLink, setZoomLink] = useState(session.zoom_link || "");
  const [oficialNotes, setOficialNotes] = useState(session.oficial_notes || "");
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState(session.status);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/simulations/${session.id}`, {
        zoom_link: zoomLink || null,
        oficial_notes: oficialNotes || null,
        status: newStatus
      });
      toast.success("Simulacro actualizado");
      onRefresh();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/simulations/${session.id}`, {
        status: "completed",
        oficial_notes: oficialNotes || null
      });
      toast.success("Simulacro marcado como completado");
      onRefresh();
    } catch (error) {
      toast.error("Error al completar");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Estás seguro de cancelar este simulacro?")) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/simulations/${session.id}`, {
        status: "cancelled"
      });
      toast.success("Simulacro cancelado");
      onRefresh();
    } catch (error) {
      toast.error("Error al cancelar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      layout
      className={`bg-white rounded-xl border overflow-hidden transition-all ${
        isExpanded ? "border-purple-300 shadow-lg" : "border-navy-100 hover:shadow-md"
      }`}
      data-testid={`simulation-card-${session.id}`}
    >
      {/* Header */}
      <div 
        className={`p-5 cursor-pointer ${isExpanded ? "bg-gradient-to-r from-purple-50 to-blue-50" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              session.status === "completed" ? "bg-emerald-100" :
              session.status === "scheduled" ? "bg-blue-100" :
              session.status === "cancelled" ? "bg-red-100" :
              "bg-amber-100"
            }`}>
              <Video className={`w-7 h-7 ${
                session.status === "completed" ? "text-emerald-600" :
                session.status === "scheduled" ? "text-blue-600" :
                session.status === "cancelled" ? "text-red-600" :
                "text-amber-600"
              }`} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-navy-900">
                  {session.client?.name || "Cliente"}
                </h3>
                <StatusBadge status={session.status} />
              </div>
              <div className="flex items-center gap-4 text-sm text-navy-500">
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  {session.client?.email}
                </span>
                {session.client?.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {session.client.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-navy-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
        </div>

        {/* Quick Info */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-navy-50 rounded-lg">
            <Calendar className="w-4 h-4 text-navy-400 mx-auto mb-1" />
            <p className="text-sm font-medium text-navy-900">
              {session.scheduled_date ? new Date(session.scheduled_date).toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'short'
              }) : "Sin agendar"}
            </p>
            <p className="text-xs text-navy-500">Fecha</p>
          </div>
          <div className="text-center p-3 bg-navy-50 rounded-lg">
            <Clock className="w-4 h-4 text-navy-400 mx-auto mb-1" />
            <p className="text-sm font-medium text-navy-900">
              {session.scheduled_time || "--:--"}
            </p>
            <p className="text-xs text-navy-500">Hora</p>
          </div>
          <div className="text-center p-3 bg-navy-50 rounded-lg">
            <Play className="w-4 h-4 text-navy-400 mx-auto mb-1" />
            <p className="text-sm font-medium text-navy-900">
              {session.duration_minutes || 30} min
            </p>
            <p className="text-xs text-navy-500">Duración</p>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-navy-100"
          >
            <div className="p-5 space-y-4">
              {/* Client Notes */}
              {session.client_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-800">Notas del Cliente</span>
                  </div>
                  <p className="text-sm text-blue-700">{session.client_notes}</p>
                </div>
              )}

              {/* Zoom Link */}
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  <LinkIcon className="w-4 h-4 inline mr-1" />
                  Link de Zoom
                </label>
                <div className="flex gap-2">
                  <Input
                    value={zoomLink}
                    onChange={(e) => setZoomLink(e.target.value)}
                    placeholder="https://zoom.us/j/..."
                    className="flex-1"
                    data-testid="zoom-link-input"
                  />
                  {zoomLink && (
                    <a href={zoomLink} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="icon">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              {/* Oficial Notes */}
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Notas del Oficial
                </label>
                <Textarea
                  value={oficialNotes}
                  onChange={(e) => setOficialNotes(e.target.value)}
                  placeholder="Observaciones sobre la sesión, feedback, etc."
                  rows={3}
                  data-testid="oficial-notes-input"
                />
              </div>

              {/* Status Update */}
              {session.status !== "completed" && session.status !== "cancelled" && (
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Actualizar Estado
                  </label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="scheduled">Agendado</SelectItem>
                      <SelectItem value="completed">Completado</SelectItem>
                      <SelectItem value="cancelled">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Client Feedback (if completed) */}
              {session.status === "completed" && session.client_feedback && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-800">
                      Feedback del Cliente {session.rating && `(${session.rating}/5)`}
                    </span>
                  </div>
                  <p className="text-sm text-emerald-700">{session.client_feedback}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-navy-100">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="save-simulation-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Guardar Cambios
                </Button>
                
                {session.status === "scheduled" && (
                  <Button
                    onClick={handleComplete}
                    disabled={saving}
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="complete-simulation-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Marcar Completado
                  </Button>
                )}
                
                {session.status !== "completed" && session.status !== "cancelled" && (
                  <Button
                    onClick={handleCancel}
                    disabled={saving}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="cancel-simulation-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SimulacionesPanel = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/simulations/pending`);
      setSessions(response.data?.sessions || []);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Error al cargar simulacros");
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(s => {
    if (filter === "all") return true;
    return s.status === filter;
  });

  const stats = {
    total: sessions.length,
    pending: sessions.filter(s => s.status === "pending").length,
    scheduled: sessions.filter(s => s.status === "scheduled").length,
    completed: sessions.filter(s => s.status === "completed").length
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="simulaciones-panel">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Video className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-navy-900">
                Gestión de Simulacros
              </h1>
              <p className="text-navy-500">Administra las sesiones de práctica de entrevista</p>
            </div>
          </div>
          <Button onClick={fetchSessions} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Video className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Total</p>
                <p className="text-2xl font-bold text-navy-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Pendientes</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Agendados</p>
                <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Completados</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-navy-700">Filtrar por:</span>
            <div className="flex gap-2">
              {[
                { value: "all", label: "Todos" },
                { value: "pending", label: "Pendientes" },
                { value: "scheduled", label: "Agendados" },
                { value: "completed", label: "Completados" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFilter(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filter === option.value
                      ? "bg-purple-600 text-white"
                      : "bg-navy-50 text-navy-600 hover:bg-navy-100"
                  }`}
                  data-testid={`filter-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sessions List */}
        {filteredSessions.length > 0 ? (
          <div className="space-y-4">
            {filteredSessions.map((session) => (
              <SimulationCard
                key={session.id}
                session={session}
                onUpdate={() => {}}
                onRefresh={fetchSessions}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
            <Video className="w-12 h-12 text-navy-300 mx-auto mb-4" />
            <p className="text-navy-500">
              {filter === "all"
                ? "No hay simulacros registrados"
                : `No hay simulacros con estado "${filter}"`}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SimulacionesPanel;
